import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { signInWithPopup } from "firebase/auth";
import { auth, googleProvider } from "../../firebaseConfig";
import { authService } from "../../services/authService";
import { useAuth } from "../../context/AuthContext";
import { notify } from "../../lib/notifications";

import GlassCard from "../../components/ui/GlassCard";
import AuthHeader from "../../components/auth/AuthHeader";
import GoogleSignInButton from "../../components/auth/GoogleSignInButton";
import LegalLinks from "../../components/auth/LegalLinks";

function extractErrorMessage(err: unknown): string {
    if (err instanceof Error && err.message) return err.message;
    return "Failed to sign in. Please try again.";
}

export default function Login() {
    const navigate = useNavigate();
    const { login } = useAuth();
    const [isLoading, setLoading] = useState(false);

    const signInWithGoogle = async () => {
        setLoading(true);
        try {
            const { user: googleUser } = await signInWithPopup(auth, googleProvider);

            const response = await authService.googleLogin(
                googleUser.email || "",
                googleUser.providerData[0]?.uid,
                googleUser.photoURL || "",
                googleUser.displayName || ""
            );

            login(response.session, response.user);
            navigate(response.is_new_user ? "/profile-setup" : "/dashboard");
        } catch (err) {
            console.error("Google sign-in failed:", err);
            notify.error(extractErrorMessage(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4 relative overflow-hidden">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-white/[0.02] rounded-full blur-[100px] pointer-events-none" />
            <div className="absolute top-0 left-0 w-full h-full bg-[url('/grid-pattern.svg')] opacity-[0.03] pointer-events-none" />

            <GlassCard className="w-full max-w-md p-8 md:p-10 relative z-10">
                <AuthHeader />

                <GoogleSignInButton onClick={signInWithGoogle} isLoading={isLoading} />

                <LegalLinks />

                <div className="text-center mt-6">
                    <motion.button
                        whileHover={{ x: -2 }}
                        onClick={() => navigate("/")}
                        className="text-sm text-zinc-500 hover:text-white transition-colors inline-flex items-center gap-1"
                    >
                        <i className="ri-arrow-left-line" />
                        Back to home
                    </motion.button>
                </div>
            </GlassCard>
        </div>
    );
}
